import { constant } from 'lodash/fp';
import styled, { css, DefaultTheme, ThemeProps } from 'styled-components';
import { DENSE_LIGHT_THEME } from '@optimus-web/theme';
import { getSpacingToken, getTextTokenPresetObject, getToken } from '@optimus-web/core';
import { COLOR_DANGER_LIGHT, COLOR_WHITE, TimeUnitMatrixCell } from '../../../../components/commander-ui';
import {
    CornerCellElement,
    matrixAsideFactory,
    MatrixAsideWrapperElement,
    MatrixCellWrapperElement,
    matrixFactory,
    matrixHeaderFactory,
    MatrixHeaderWrapperElement,
} from '../../../../components/commander-ui/Matrix';
import { BORDER_THICKNESS } from '../../constants';
import { createBodyCellColorMapper } from '../../utils';

const getBorderStyle = ({ theme }: ThemeProps<DefaultTheme>) =>
    `${BORDER_THICKNESS}px solid ${getToken('borderStaticSecondary')({ theme })}`;
const getDarkBorderStyle = ({ theme }: ThemeProps<DefaultTheme>) =>
    `${BORDER_THICKNESS}px solid ${getToken('borderAccent')({ theme })}`;

const CornerCell = styled(CornerCellElement)`
    border-bottom: ${getDarkBorderStyle};
    border-right: ${getDarkBorderStyle};
`;

const MatrixAsideWrapper = styled(MatrixAsideWrapperElement)`
    border-bottom: ${getBorderStyle};
    border-right: ${getDarkBorderStyle};
`;

const MatrixHeaderWrapper = styled(MatrixHeaderWrapperElement)`
    border-bottom: ${getDarkBorderStyle};
    border-right: ${getBorderStyle};
    border-top: ${getBorderStyle};
`;

export const RestrictionsMatrixElement = matrixFactory({
    MatrixAside: matrixAsideFactory({ CornerCell, MatrixAsideWrapper }),
    MatrixHeader: matrixHeaderFactory({ MatrixHeaderWrapper }),
});

const CommonCellElement = styled(MatrixCellWrapperElement)`
    padding: ${getSpacingToken('100')}px;
`;

export const AsideCellLabelElement = styled(CommonCellElement)`
    flex-basis: 40%;
    ${(props) =>
        (props as $TSFixMe).bold &&
        `font-weight: ${getTextTokenPresetObject('titleSmallStrong')({ theme: props.theme }).fontWeight};`}
    ${(props) => (props as $TSFixMe).borderBottom && `border-bottom: ${getBorderStyle(props)};`}
    ${(props) => (props as $TSFixMe).borderLeft && `border-left: ${getBorderStyle(props)};`}
`;

export const CloseTypeCellElement = styled(CommonCellElement)`
    background: ${getToken('backgroundInteractiveNeutralSubtleActive')};
    border-bottom: ${getBorderStyle};
    border-left: ${getBorderStyle};
    flex-basis: 20%;
    font-weight: ${(props) => getTextTokenPresetObject('titleSmallStrong')({ theme: props.theme }).fontWeight};
    justify-content: center;
`;

export const RestrictionHeaderCellElement = styled(TimeUnitMatrixCell)`
    border-right: ${getBorderStyle};
    padding: ${getSpacingToken('50')}px ${getSpacingToken('100')}px;
    text-align: inherit;
`;

const getSelectedCellStyle = ({ theme }: ThemeProps<DefaultTheme>) =>
    `box-shadow: inset 0px 0px 0px ${BORDER_THICKNESS}px ${getToken('paletteBrandGrey700')({ theme })};`;

const getBodyCellBackground = createBodyCellColorMapper({
    onClosed: constant(COLOR_DANGER_LIGHT),
    onOpened: constant(getToken('paletteSemanticGreen100')({ theme: DENSE_LIGHT_THEME })),
    onDefault: constant(COLOR_WHITE),
});

const getBodyCellHoverBackground = createBodyCellColorMapper({
    onClosed: constant(getToken('paletteSemanticRed100')({ theme: DENSE_LIGHT_THEME })),
    onOpened: constant(getToken('paletteSemanticGreen200')({ theme: DENSE_LIGHT_THEME })),
    onDefault: constant(getToken('backgroundInteractiveNeutralSubtleActive')({ theme: DENSE_LIGHT_THEME })),
});

export const RestrictionsBodyCellElement = styled(CommonCellElement)`
    background: ${
        // @ts-expect-error $TSFixMeError: No overload matches this call.
        getBodyCellBackground
    };
    border-bottom: ${getBorderStyle};
    border-right: ${getBorderStyle};
    padding: ${getSpacingToken('200')}px;
    ${(props) => (props as $TSFixMe).isSelected && getSelectedCellStyle(props)}
    ${(props) =>
        (props as $TSFixMe).hasExceptions || (props as $TSFixMe).isClosed
            ? css`
                  cursor: pointer;
                  &:hover {
                      background: ${getBodyCellHoverBackground(props) as $TSFixMe};
                  }
              `
            : css`
                  pointer-events: none;
              `}
`;
